/**
 * Edit a single lesson's `content` (`LessonBlock[]`).
 *
 * Implements the block builder called out in Task #18: add / remove /
 * reorder typed content blocks, with type-specific inputs. Paragraph and
 * callout body fields use the existing Quill `EmailBodyEditor` so authors
 * get rich text without a parallel editor implementation.
 *
 * Quill emits HTML; for paragraph blocks we store that HTML in
 * `block.text`. The learner renderer renders paragraphs via
 * dangerouslySetInnerHTML so the formatting comes through. Plain seed
 * content (no tags) renders unchanged.
 */
import type { LessonBlock } from '@xsc/shared';
import { Button } from '../ui/button.js';
import { Input, Textarea } from '../ui/input.js';
import { EmailBodyEditor } from '../email-body-editor.js';

interface Props {
  value: LessonBlock[];
  onChange: (next: LessonBlock[]) => void;
}

// SCORM is intentionally excluded — it's added by the import widget which
// uploads the zip first and creates the block server-side.
const TYPE_OPTIONS: { value: Exclude<LessonBlock['type'], 'scorm'>; label: string }[] = [
  { value: 'heading', label: 'Heading' },
  { value: 'paragraph', label: 'Paragraph (rich)' },
  { value: 'callout', label: 'Callout' },
  { value: 'list', label: 'List' },
  { value: 'table', label: 'Table' },
  { value: 'exercise', label: 'Exercise' },
  { value: 'video', label: 'Video' },
  { value: 'divider', label: 'Divider' },
];

function blank(type: LessonBlock['type']): LessonBlock {
  switch (type) {
    case 'heading':   return { type: 'heading', text: '', level: 2 };
    case 'paragraph': return { type: 'paragraph', text: '' };
    case 'callout':   return { type: 'callout', title: '', items: [''], variant: 'info' };
    case 'list':      return { type: 'list', items: [''], ordered: false };
    case 'table':     return { type: 'table', headers: ['Col 1'], rows: [['']] };
    case 'exercise':  return { type: 'exercise', title: '', instructions: '' };
    case 'video':     return { type: 'video', provider: 'mp4', url: '', title: '', duration_sec: 0 };
    case 'divider':   return { type: 'divider' };
    // SCORM blocks are not author-creatable from the dropdown — they're
    // produced by the SCORM import widget on the lesson editor. We still
    // need a no-op so the union switch is exhaustive.
    case 'scorm':     return { type: 'scorm', package_id: '' };
  }
}

export function LessonBlockEditor({ value, onChange }: Props): JSX.Element {
  const update = (idx: number, next: LessonBlock) => {
    onChange(value.map((b, i) => (i === idx ? next : b)));
  };
  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= value.length) return;
    const next = value.slice();
    const tmp = next[idx]!;
    next[idx] = next[j]!;
    next[j] = tmp;
    onChange(next);
  };
  const add = (type: LessonBlock['type']) => onChange([...value, blank(type)]);

  return (
    <div className="space-y-3">
      {value.length === 0 && (
        <p className="text-sm text-ink-muted">No content blocks yet. Add one below.</p>
      )}
      {value.map((block, idx) => (
        <div key={idx} className="border border-border rounded p-3 bg-surface">
          <div className="flex items-center justify-between mb-2 gap-2">
            <span className="text-xs uppercase tracking-wide text-ink-muted">
              #{idx + 1} · {block.type}
            </span>
            <div className="flex gap-1">
              <button type="button" className="text-xs px-2 py-1 rounded border border-border"
                onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up">↑</button>
              <button type="button" className="text-xs px-2 py-1 rounded border border-border"
                onClick={() => move(idx, 1)} disabled={idx === value.length - 1} title="Move down">↓</button>
              <button type="button" className="text-xs px-2 py-1 rounded border border-border text-danger"
                onClick={() => remove(idx)} title="Delete">✕</button>
            </div>
          </div>
          <BlockBody block={block} onChange={(b) => update(idx, b)} />
        </div>
      ))}

      <div className="flex flex-wrap gap-1 pt-1">
        <span className="text-xs text-ink-muted self-center pr-1">Add:</span>
        {TYPE_OPTIONS.map((t) => (
          <Button key={t.value} type="button" variant="secondary" onClick={() => add(t.value)}>
            + {t.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function BlockBody({
  block,
  onChange,
}: {
  block: LessonBlock;
  onChange: (b: LessonBlock) => void;
}): JSX.Element {
  switch (block.type) {
    case 'heading':
      return (
        <div className="flex gap-2 items-center">
          <select
            className="rounded border border-border bg-surface px-2 py-1 text-sm"
            value={block.level ?? 2}
            onChange={(e) => onChange({ ...block, level: Number(e.target.value) as 2 | 3 })}
          >
            <option value={2}>H2</option>
            <option value={3}>H3</option>
          </select>
          <Input
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder="Heading text"
          />
        </div>
      );

    case 'paragraph':
      return (
        <EmailBodyEditor
          value={block.text}
          onChange={(html) => onChange({ ...block, text: html })}
          placeholder="Paragraph text. Use the toolbar to format."
        />
      );

    case 'callout':
      return (
        <div className="space-y-2">
          <div className="flex gap-2">
            <select
              className="rounded border border-border bg-surface px-2 py-1 text-sm"
              value={block.variant ?? 'info'}
              onChange={(e) =>
                onChange({ ...block, variant: e.target.value as 'info' | 'warning' | 'success' | 'danger' })
              }
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="success">Success</option>
              <option value="danger">Danger</option>
            </select>
            <Input
              value={block.title}
              onChange={(e) => onChange({ ...block, title: e.target.value })}
              placeholder="Callout title"
            />
          </div>
          <RichList
            items={block.items}
            onChange={(items) => onChange({ ...block, items })}
            placeholder="Callout line (rich text)"
          />
        </div>
      );

    case 'list':
      return (
        <div className="space-y-2">
          <label className="text-xs flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!block.ordered}
              onChange={(e) => onChange({ ...block, ordered: e.target.checked })}
            />
            Ordered list (numbered)
          </label>
          <StringList
            items={block.items}
            onChange={(items) => onChange({ ...block, items })}
            placeholder="List item"
          />
        </div>
      );

    case 'table': {
      const setHeader = (i: number, v: string) =>
        onChange({ ...block, headers: block.headers.map((h, j) => (i === j ? v : h)) });
      const setCell = (r: number, c: number, v: string) =>
        onChange({
          ...block,
          rows: block.rows.map((row, ri) =>
            ri !== r ? row : row.map((cell, ci) => (ci === c ? v : cell)),
          ),
        });
      const addCol = () => onChange({
        ...block,
        headers: [...block.headers, `Col ${block.headers.length + 1}`],
        rows: block.rows.map((r) => [...r, '']),
      });
      const addRow = () => onChange({
        ...block,
        rows: [...block.rows, block.headers.map(() => '')],
      });
      return (
        <div className="space-y-2 overflow-x-auto">
          <table className="border-collapse">
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className="border border-border p-1">
                    <Input value={h} onChange={(e) => setHeader(i, e.target.value)} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="border border-border p-1">
                      <Input value={cell} onChange={(e) => setCell(r, c, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={addCol}>+ Column</Button>
            <Button type="button" variant="secondary" onClick={addRow}>+ Row</Button>
          </div>
        </div>
      );
    }

    case 'exercise':
      return (
        <div className="space-y-2">
          <Input
            value={block.title}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            placeholder="Exercise title"
          />
          <Textarea
            value={block.instructions}
            onChange={(e) => onChange({ ...block, instructions: e.target.value })}
            placeholder="Instructions"
            rows={3}
          />
          <Input
            value={block.prompt ?? ''}
            onChange={(e) => onChange({ ...block, prompt: e.target.value || undefined })}
            placeholder="Prompt (optional)"
          />
        </div>
      );

    case 'video':
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-[10rem_1fr] gap-2">
            <select
              className="rounded border border-border bg-surface px-2 py-1 text-sm"
              value={block.provider}
              onChange={(e) =>
                onChange({ ...block, provider: e.target.value as 'mp4' | 'youtube' | 'vimeo' })
              }
            >
              <option value="mp4">MP4 / WebM URL</option>
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
            </select>
            <Input
              value={block.url}
              placeholder="https://… (must be https in production)"
              onChange={(e) => onChange({ ...block, url: e.target.value })}
            />
          </div>
          <Input
            value={block.title ?? ''}
            placeholder="Video title (optional, shown above the player)"
            onChange={(e) => onChange({ ...block, title: e.target.value })}
          />
          <p className="text-xs text-ink-muted">
            Learners must watch ≥ 90% of any video block before the lesson can be marked complete.
          </p>
        </div>
      );

    case 'divider':
      return <p className="text-xs text-ink-muted">Horizontal divider — no content.</p>;

    case 'scorm':
      return (
        <div className="space-y-1">
          <div className="text-sm">
            Linked SCORM/xAPI package:{' '}
            <code className="text-xs">{block.package_id}</code>
            {block.title && <> · <span className="text-ink-muted">{block.title}</span></>}
          </div>
          <p className="text-xs text-ink-muted">
            The package and its files live in object storage. Re-import on the
            lesson to replace; use the ✕ above to detach the block (the package
            row stays — delete it via the API to free storage).
          </p>
        </div>
      );
  }
}

function RichList({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}): JSX.Element {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-1 items-start">
          <div className="flex-1">
            <EmailBodyEditor
              value={item}
              onChange={(html) => onChange(items.map((v, j) => (i === j ? html : v)))}
              placeholder={placeholder}
            />
          </div>
          <button
            type="button"
            className="text-xs px-2 py-1 rounded border border-border text-danger shrink-0"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            title="Remove"
          >✕</button>
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={() => onChange([...items, ''])}>
        + Add line
      </Button>
    </div>
  );
}

function StringList({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}): JSX.Element {
  return (
    <div className="space-y-1">
      {items.map((item, i) => (
        <div key={i} className="flex gap-1">
          <Input
            value={item}
            onChange={(e) => onChange(items.map((v, j) => (i === j ? e.target.value : v)))}
            placeholder={placeholder}
          />
          <button
            type="button"
            className="text-xs px-2 py-1 rounded border border-border text-danger"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            title="Remove"
          >✕</button>
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={() => onChange([...items, ''])}>
        + Add line
      </Button>
    </div>
  );
}
